/**
 * SubPageShell — shared page frame for standalone routes (/pricing,
 * /products, /portfolio, /tos). Handles config fetch, loading state, and
 * wraps the section in SiteNav + SiteFooter chrome (matching the homepage).
 *
 * Usage:
 *   <SubPageShell>
 *     {(config) => <PricingBlock tiers={config.tiers} />}
 *   </SubPageShell>
 */
import { useEffect, useState } from "react";
import { fetchConfig } from "@/api";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import MouseParticles from "@/components/MouseParticles";

export default function SubPageShell({ children, testId }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    fetchConfig()
      .then(setConfig)
      .catch((e) => setErr(e.message || "Failed to load"))
      .finally(() => setLoading(false));
    // Ensure subpages start scrolled to the top (they replace, not append)
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--theme-bg)] px-6">
        <div className="flex flex-col items-center gap-4 text-center" data-testid="subpage-loader">
          <img src="/logo.png?v=4" alt="" className="h-10 w-10 object-contain opacity-70" />
          <div className="font-mono text-[10px] tracking-[0.4em] text-[var(--theme-text-muted)] uppercase">
            Loading
          </div>
        </div>
      </div>
    );
  }

  if (err || !config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="font-mono text-red-500 text-sm">! ERROR: {err}</div>
      </div>
    );
  }

  return (
    <div
      data-testid={testId}
      className="min-h-screen bg-[var(--theme-bg)] text-[var(--theme-text)] relative"
    >
      <MouseParticles count={1500} />
      <div className="nm-vignette" />
      <SiteNav config={config} />
      <div className="relative z-10 pt-16">
        {typeof children === "function" ? children(config) : children}
      </div>
      <SiteFooter config={config} />
    </div>
  );
}
